import has from 'lodash/has';

import AuthModel from '@core/auth/model';

import PermissionEnum from '@constants/enums/auth/permission';

import { uuid } from '@utils/string';

import config from '@/config';
import Organisation from '@/models/Organisation';

export function Auth(params) {
    let auth = AuthModel.all().find(au => !!au.id);

    if (!auth) {
        auth = new AuthModel(params);
    }

    return auth;
}

export function isInvestor(organisation) {
    return organisation && has(organisation, 'type') && organisation.type === 'INVESTOR';
}

export function isAdviser(organisation) {
    return organisation && has(organisation, 'type') && organisation.type === 'ADVISER';
}

export function isProvider(organisation) {
    return organisation && has(organisation, 'type') && organisation.type === 'PRODUCT_PROVIDER';
}

export function isFundManager(organisation) {
    return organisation && has(organisation, 'type') && organisation.type === 'FUND_MANAGER';
}

export function isCompany(organisation) {
    return organisation && has(organisation, 'type') && organisation.type === 'COMPANY';
}

export function getHomeRoute(organisation) {
    if (isFundManager(organisation) || isProvider(organisation)) {
        return { name: 'OffersAll' };
    } else if (isCompany(organisation)) {
        return { name: 'OffersAll' };
    }

    return { name: 'Dashboard' };
}

export function getLocalOrganisation() {
    const currentOrg = localStorage.getItem('currentOrganisation');

    if (!currentOrg) {
        return null;
    }

    if (typeof currentOrg === 'string' && currentOrg.includes(',')) {
        return currentOrg.split(',').pop();
    }

    return currentOrg;
}

export function getLocalAccount() {
    const currentAccount = localStorage.getItem('currentAccount');

    if (!currentAccount) {
        return null;
    }

    if (typeof currentAccount === 'string' && currentAccount.includes(',')) {
        return currentAccount.split(',').pop();
    }

    return currentAccount;
}

export function getCurrentOrganisation(user = null) {
    if (!user) {
        return null;
    }

    const currentOrgId = getLocalOrganisation();

    if (!currentOrgId) {
        return null;
    }

    return user.organisations.find(org => org.id === currentOrgId) || null;
}

export function getCurrentAccount(organisation = null) {
    if (!organisation || organisation.type !== 'INVESTOR') {
        return null;
    }

    const currentAccountId = getLocalAccount();

    if (!currentAccountId) {
        return null;
    }

    return organisation.accounts.find(acc => acc.adviser_id === currentAccountId) || null;
}

export function redirectToLogin(params = {}, newTab = false) {
    const loginUrl = new URL('/oauth2/start', config.API_BASE_URL);

    if (newTab) {
        if ('rd' in params) {
            const rdUrl = new URL(params.rd);
            rdUrl.searchParams.append('closeAfterSuccessfulLogin', true);
            params.rd = rdUrl.href;
        }

        for (const key in params) {
            loginUrl.searchParams.append(key, params[`${key}`]);
        }

        window.open(loginUrl.href);
    } else {
        for (const key in params) {
            loginUrl.searchParams.append(key, params[`${key}`]);
        }

        window.location.href = loginUrl.href;
    }
}

export function redirectToLogout(returnUrl, signoutUrl = null) {
    const logoutUrl = new URL('/oauth2/sign_out', config.API_BASE_URL);

    if (signoutUrl) {
        signoutUrl = new URL(signoutUrl);
        signoutUrl.searchParams.append('redirect_uri', returnUrl);
        logoutUrl.searchParams.append('rd', signoutUrl.href);
    }

    localStorage.removeItem('currentOrganisation');
    localStorage.removeItem('currentAccount');
    localStorage.removeItem('currentTermsAccepted');
    window.location.href = logoutUrl.href;
}

export function changeContext(organisation, account) {
    const rd = new URL(window.location.href);

    if (has(account, 'base_url') && account.base_url) {
        rd.host = account.base_url;
    } else if (has(organisation, 'base_url') && organisation.base_url) {
        rd.host = organisation.base_url;
    }

    rd.searchParams.append('currentOrganisation', organisation.id);

    if (account) {
        rd.searchParams.append('currentAccount', account.adviser_id);
    }

    localStorage.removeItem('currentTermsAccepted');
    window.location.href = rd.href;
}

export function changeLogin(user, organisation, account) {
    const rd = new URL(window.location.href);

    const params = {
        login_hint: user.email,
        prompt: 'login',
        app_uri: organisation.base_url
    };

    rd.searchParams.append('currentOrganisation', organisation.id);

    if (account) {
        params.app_uri = account.base_url;
        rd.searchParams.append('currentAccount', account.adviser_id);
        localStorage.removeItem('currentTermsAccepted');
    }

    rd.host = params.app_uri;
    params.rd = rd.toString();

    redirectToLogin(params);
}

export function hasTierAccess(organisation, level) {
    if (!organisation) {
        return false;
    }

    if (!(organisation instanceof Organisation)) {
        organisation = new Organisation(organisation);
    }

    return organisation.hasTierAccess(level);
}

export function logout() {
    return redirectToLogout(window.location.origin, Auth().signout_url);
}

export function mockUser(params = {}) {
    return {
        id: uuid(36),
        name: 'Mock User',
        email: 'mock-user@email.com',
        title: null,
        gi_ref: null,
        signout_url: 'https://logout.test',
        organisations: [mockOrganisation()],
        linked_users: [],
        ...params
    };
}

export function mockOrganisation(params = {}) {
    if ('type' in params && params.type === 'INVESTOR' && !('accounts' in params)) {
        params.accounts = [mockAccount()];
    }

    return {
        id: uuid(36),
        name: 'Mock Organisation',
        type: 'ADVISER',
        roles: ['PLATFORM_SUPERADMIN'],
        permissions: Object.keys(PermissionEnum),
        base_url: 'app.growthinvest.localhost',
        ...params
    };
}

export function mockAccount(params = {}) {
    return {
        adviser_id: uuid(36),
        adviser_name: 'Mock Adviser',
        base_url: 'app.growthinvest.localhost',
        ...params
    };
}
